import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AccessibleButton from './AccessibleButton'
import { useUserContext } from '@/context/UserContext'
import { Address } from '../types/quoteTypes'
import { postcodeSearch } from '@/utils/addressSearch'


interface PostcodeQuoteFormProps {
  onQuoteRequest?: (postcode: string, address: string) => void
  className?: string
}

const PostcodeQuoteForm = ({ onQuoteRequest, className = '' }: PostcodeQuoteFormProps) => {
  const navigate = useNavigate()

  const userContext = useUserContext()
  const [postcodeInput, setPostcodeInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const validatePostcode = (code: string): boolean => {
    const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i
    return postcodeRegex.test(code.replace(/\s/g, ''))
  }

  const formatPostcode = (code: string): string => {
    const cleaned = code.replace(/\s/g, '').toUpperCase()
    if (cleaned.length >= 4) {
      return cleaned.slice(0, -3) + ' ' + cleaned.slice(-3)
    }
    return cleaned
  }

  const searchAddresses = async (searchPostcode: string) => {
    if (!validatePostcode(searchPostcode)) {
      setError('Please enter a valid UK postcode')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const results = await postcodeSearch(searchPostcode)
      userContext.setAddresses(results)
      userContext.setShowAddresses(true)
    } catch {
      setError('Unable to find addresses for this postcode. Please check and try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePostcodeSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (postcodeInput.trim()) {
      const formatted = formatPostcode(postcodeInput.trim())
      setPostcodeInput(formatted)
      searchAddresses(formatted)
    }
  }

  const handleAddressSelect = (address: Address) => {
    userContext.setSelectedAddress(address)
    userContext.setShowAddresses(false)

    if (onQuoteRequest) {
      onQuoteRequest(address.postcode, address.formatted_address.join(', '))
    }
  }

  const handleReset = () => {
    setPostcodeInput('')
    userContext.setAddresses([])
    userContext.setSelectedAddress(null)
    userContext.setShowAddresses(false)
    setError('')
  }

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000)
      return () => clearTimeout(timer)
    }
  }, [error])

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-dark-plum mb-2">
          Get Your Free Quote
        </h3>
        <p className="text-sm text-gray-600">
          Enter your postcode to find your address and get an instant quote
        </p>
      </div>

      {!userContext.selectedAddress ? (
        <form onSubmit={handlePostcodeSubmit} className="space-y-4">
          <div>
            <label htmlFor="postcode-input" className="sr-only">
              Enter your postcode
            </label>
            <div className="flex gap-2">
              <input
                id="postcode-input"
                type="text"
                value={postcodeInput}
                onChange={(e) => setPostcodeInput(e.target.value.toUpperCase())}
                placeholder="Enter postcode (e.g. SW1A 1AA)"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent text-gray-900 placeholder-gray-500"
                disabled={isLoading}
                maxLength={8}
              />
              <AccessibleButton
                type="submit"
                disabled={isLoading || !postcodeInput.trim()}
                variant="primary"
                className="px-4 py-2 min-w-[80px]"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  </div>
                ) : (
                  'Search'
                )}
              </AccessibleButton>
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg" role="alert">
              {error}
            </div>
          )}

          {userContext.showAddresses && userContext.addresses.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Select your address:
              </p>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {userContext.addresses.map((address) => (
                  <button
                    key={address.id}
                    type="button"
                    onClick={() => handleAddressSelect(address)}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-primary-green hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-primary-green transition-all duration-200"
                  >
                    <div className="text-sm">
                      {address.formatted_address.map((line, index) => (
                        <div key={index} className={index === 0 ? 'font-medium text-gray-600' : 'text-gray-600'}>
                          {line}
                        </div>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>
      ) : (
        <div className="space-y-4">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <p className="text-sm font-medium text-green-800 mb-2">Selected Address:</p>
            <p className="text-sm text-green-700">{userContext.selectedAddress.formatted_address.join(', ')}</p>
          </div>

          <div className="flex gap-2">
            <AccessibleButton
              onClick={handleReset}
              variant="secondary"
              className="flex-1"
            >
              Change Address
            </AccessibleButton>
            <AccessibleButton
              onClick={() => {
                if (onQuoteRequest) {
                  onQuoteRequest(userContext.selectedAddress!.postcode, userContext.selectedAddress!.formatted_address.join(', '))
                }
                navigate('/quote')
              }}
              variant="primary"
              className="flex-1"
            >
              Get My Quote
            </AccessibleButton>
          </div>
        </div>
      )}
    </div>
  )
}

export default PostcodeQuoteForm
